import type { JSONConfig } from '@/engine/app/interfaces.ts';

import { SceneItemKind } from '@/engine/app/interfaces.ts';
import { ImagesMode, SliceType } from '@/engine/app/modules/images/interfaces.ts';

export const aquaColor = '#00FFFF';
export const fuchsiaColor = '#ff008a';
export const neonGreenColor = '#0FFF50';
export const neonLemonColor = '#FFD700';
const lightCoralColor = '#FFBBBB';
const lightYellow = '#FFFFDD';
export const magentaColor = '#FF00FF';
const lemonColor = '#FFFF77';
export const deepSkyBlueColor = '#00C1FF';
export const deepSkyBlueColorDarken = '#008fe1';
const peachColor = '#FFE5C5';
export const blueColor = '#0000FF';
export const neonBlueColor = '#96abfe';
export const redColor = '#ff0000';
export const darkMetallicGray = '#3A3A3A';

export const catheter: JSONConfig.Object = {
  descriptor: 'catheter',
  label: 'Catheter',
  color: lightYellow,
  kind: SceneItemKind.Mesh,
  isFluoroOverlay: true
};

export const valve: JSONConfig.Object = {
  descriptor: 'valve',
  label: 'Valve',
  color: '#FFFFAA',
  kind: SceneItemKind.Mesh,
  imagesOverlayColor: '#0000AA',
  isFluoroOverlay: true
};

export const arteriesAndKidneys: JSONConfig.Object = {
  descriptor: 'arteries_kidneys',
  label: 'Arteries & Kidneys',
  color: lightCoralColor,
  kind: SceneItemKind.Mesh
};

export const vessel: JSONConfig.Object = {
  descriptor: 'vessel',
  label: 'Veins',
  color: lightCoralColor,
  kind: SceneItemKind.Mesh
};

export const vesselColored: JSONConfig.Object = {
  descriptor: 'vessel.colored',
  label: 'Veins',
  color: lightCoralColor,
  isVertexColoring: true,
  kind: SceneItemKind.Mesh
};

export const bones: JSONConfig.Object = {
  descriptor: 'bones',
  label: 'Bones',
  color: peachColor,
  kind: SceneItemKind.Mesh
};

export const filter: JSONConfig.Object = {
  descriptor: 'filter',
  label: 'Filter',
  color: darkMetallicGray,
  kind: SceneItemKind.Mesh
};

export const hardware: JSONConfig.Object = {
  descriptor: 'hardware',
  label: 'Hardware',
  color: darkMetallicGray,
  kind: SceneItemKind.Mesh
};

export const leftAtrium: JSONConfig.Object = {
  descriptor: 'left_atrium',
  label: 'Left Atrium',
  color: magentaColor,
  kind: SceneItemKind.Mesh
};

export const leftVentricle: JSONConfig.Object = {
  descriptor: 'left_ventricle',
  label: 'Left Ventricle',
  color: '#ffcdcd',
  kind: SceneItemKind.Mesh
};

export const aorta: JSONConfig.Object = {
  descriptor: 'aorta',
  label: 'Aorta',
  color: lightCoralColor,
  kind: SceneItemKind.Mesh
};

export const pulmonaryArtery: JSONConfig.Object = {
  descriptor: 'pulmonary_artery',
  label: 'Pulmonary Artery',
  color: blueColor,
  kind: SceneItemKind.Mesh
};

export const rightAtrium: JSONConfig.Object = {
  descriptor: 'right_atrium',
  attachments: ['var_ra_volume'],
  label: 'Right Atrium',
  color: aquaColor,
  kind: SceneItemKind.Mesh
};

export const rightVentricle: JSONConfig.Object = {
  descriptor: 'right_ventricle',
  attachments: ['var_rv_volume'],
  label: 'Right Ventricle',
  color: '#9170ff',
  kind: SceneItemKind.Mesh
};

export const rightHeart: JSONConfig.Object = {
  descriptor: 'right_heart',
  label: 'Right Heart',
  color: neonBlueColor,
  kind: SceneItemKind.Mesh
};

export const pt_AnteroseptalCommissure: JSONConfig.Object = {
  descriptor: 'pt_as_imported',
  label: 'Anteroseptal commissure',
  color: aquaColor,
  kind: SceneItemKind.Point
};

export const pt_AnteroposteriorCommissure: JSONConfig.Object = {
  descriptor: 'pt_ap_imported',
  label: 'Anteroposterior commissure',
  color: aquaColor,
  kind: SceneItemKind.Point
};

export const pt_PosteroseptalCommissure: JSONConfig.Object = {
  descriptor: 'pt_ps_imported',
  label: 'Posteroseptal commissure',
  color: aquaColor,
  kind: SceneItemKind.Point
};

export const landingZoneSpline: JSONConfig.Object = {
  descriptor: 'landing_zone_spline',
  label: 'Landing Zone Spline',
  color: '#93ff3c',
  highlightColor: '#ffdb85',
  selectionColor: '#fff000',
  syncSliceDescriptor: SliceType.SAGITTAL,
  attachments: ['landing_zone_area'],
  kind: SceneItemKind.Spline
};

export const eustachianRidgeSpline: JSONConfig.Object = {
  descriptor: 'tp.eustachian_ridge_spline.pts',
  label: 'Eustachian Ridge',
  color: '#ff8844',
  kind: SceneItemKind.SplineCurve
};

export const vesselCenterline: JSONConfig.Object = {
  descriptor: 'vessel.centerline',
  label: 'Veins Centerline Tree',
  color: blueColor,
  kind: SceneItemKind.LineSegmentsMesh
};

export const tp_Pl_CustomDiameter: JSONConfig.Object = {
  descriptor: 'tp.pl_femoral_vessel_custom',
  label: 'Custom Diameter Plane',
  color: '#E5A100',
  kind: SceneItemKind.CustomShiftingPlane,
  attachments: ['tp.pl_femoral_vessel_custom.area']
};

export const tp_BasalRvA: JSONConfig.Object = {
  descriptor: 'tp.pt_basal_rv_a',
  label: 'Basal RV A Point',
  color: aquaColor,
  selectionColor: deepSkyBlueColor,
  highlightColor: aquaColor,
  attachments: ['var_basal_rv_ap'],
  kind: SceneItemKind.PointOnLine
};

export const tp_BasalRvP: JSONConfig.Object = {
  descriptor: 'tp.pt_basal_rv_p',
  label: 'Basal RV P Point',
  color: aquaColor,
  selectionColor: deepSkyBlueColor,
  highlightColor: aquaColor,
  attachments: ['var_basal_rv_ap'],
  kind: SceneItemKind.PointOnLine
};

export const tp_BasalRvS: JSONConfig.Object = {
  descriptor: 'tp.pt_basal_rv_s',
  label: 'Basal RV S Point',
  color: aquaColor,
  selectionColor: deepSkyBlueColor,
  highlightColor: aquaColor,
  attachments: ['var_basal_rv_sl'],
  kind: SceneItemKind.PointOnLine
};

export const tp_BasalRvL: JSONConfig.Object = {
  descriptor: 'tp.pt_basal_rv_l',
  label: 'Basal RV L Point',
  color: aquaColor,
  selectionColor: deepSkyBlueColor,
  highlightColor: aquaColor,
  attachments: ['var_basal_rv_sl'],
  kind: SceneItemKind.PointOnLine
};

export const tp_BasalRvTvA: JSONConfig.Object = {
  descriptor: 'tp.pt_basal_rv_tv_a',
  label: 'Basal RV TV A Point',
  color: aquaColor,
  selectionColor: deepSkyBlueColor,
  highlightColor: aquaColor,
  attachments: ['var_basal_rv_tv_ap'],
  kind: SceneItemKind.PointOnLine
};

export const tp_BasalRvTvP: JSONConfig.Object = {
  descriptor: 'tp.pt_basal_rv_tv_p',
  label: 'Basal RV TV P Point',
  color: aquaColor,
  selectionColor: deepSkyBlueColor,
  highlightColor: aquaColor,
  attachments: ['var_basal_rv_tv_ap'],
  kind: SceneItemKind.PointOnLine
};

export const tp_BasalRvTvS: JSONConfig.Object = {
  descriptor: 'tp.pt_basal_rv_tv_s',
  label: 'Basal RV TV S Point',
  color: aquaColor,
  selectionColor: deepSkyBlueColor,
  highlightColor: aquaColor,
  attachments: ['var_basal_rv_tv_sl'],
  kind: SceneItemKind.PointOnLine
};

export const tp_BasalRvTvL: JSONConfig.Object = {
  descriptor: 'tp.pt_basal_rv_tv_l',
  label: 'Basal RV TV L Point',
  color: aquaColor,
  selectionColor: deepSkyBlueColor,
  highlightColor: aquaColor,
  attachments: ['var_basal_rv_tv_sl'],
  kind: SceneItemKind.PointOnLine
};

export const tp_RaHeightPoint: JSONConfig.Object = {
  descriptor: 'tp.pt_ra_height',
  label: 'RA Height Point',
  color: aquaColor,
  selectionColor: deepSkyBlueColor,
  highlightColor: aquaColor,
  attachments: ['var_ra_height'],
  kind: SceneItemKind.CuspPoint
};

export const tp_RvDepthCoaxialPoint: JSONConfig.Object = {
  descriptor: 'tp.pt_rv_depth',
  label: 'RV Depth Point (coaxial)',
  color: aquaColor,
  selectionColor: deepSkyBlueColor,
  highlightColor: aquaColor,
  attachments: ['var_rv_depth'],
  kind: SceneItemKind.CuspPoint
};

export const tp_RvDepthApexPoint: JSONConfig.Object = {
  descriptor: 'tp.pt_rv_depth_apex',
  label: 'RV Depth Point (apex)',
  color: aquaColor,
  selectionColor: deepSkyBlueColor,
  highlightColor: aquaColor,
  attachments: ['var_rv_depth_apex'],
  kind: SceneItemKind.CuspPoint
};

export const tp_LeftFemoralArteryReferencePoint: JSONConfig.Object = {
  descriptor: 'tp.pt_left_femoral_vessel',
  label: 'Left Femoral Artery Reference Point',
  color: aquaColor,
  kind: SceneItemKind.CuspPoint,
  imagesMode: ImagesMode.MPR
};

export const tp_CenterlineReferencePoint: JSONConfig.Object = {
  descriptor: 'tp.pt_centerline_reference',
  label: 'Centerline Reference Point',
  color: aquaColor,
  kind: SceneItemKind.CuspPoint,
  imagesMode: ImagesMode.MPR
};

export const tp_RightFemoralArteryReferencePoint: JSONConfig.Object = {
  descriptor: 'tp.pt_right_femoral_vessel',
  label: 'Right Femoral Artery Reference Point',
  color: aquaColor,
  kind: SceneItemKind.CuspPoint,
  imagesMode: ImagesMode.MPR
};

export const additionalDevice: JSONConfig.Object = {
  descriptor: 'additional_device',
  label: 'Additional Device',
  color: lemonColor,
  kind: SceneItemKind.Mesh
};

export const additionalStructure: JSONConfig.Object = {
  descriptor: 'additional_structure',
  label: 'Additional Structure',
  color: lemonColor,
  kind: SceneItemKind.Mesh,
  isFluoroOverlay: true
};

export const calcification: JSONConfig.Object = {
  descriptor: 'calcification',
  label: 'Calcification',
  color: lemonColor,
  kind: SceneItemKind.Mesh
};

export const veinsCalcification: JSONConfig.Object = {
  descriptor: 'vessel_calcification',
  label: 'Veins Calcification',
  color: '#FFFF92',
  kind: SceneItemKind.Mesh
};
